import type { ColumnDef } from '@tanstack/react-table';
import type { TFunction } from 'i18next';
import type {
  Candidate,
  CandidateSource,
  JobOffer,
  PipelineStage,
} from '@/models/job';
import type { ExamConfig } from '@/types/job.types';
import { Typography } from '@/components/ui/atoms/Typography';
import { Tag } from '@/components/ui/atoms/Tag';
import { StatusPill } from '@/components/ui/atoms/StatusPill';
import { getCandidateStatusVariant } from '@/components/ui/atoms/StatusPill/statusPill.utils';
import { dataTableSortHeader } from '@/components/ui/molecules/DataTable';
import {
  columnFilterHeader,
  resolveColumnFilterFn,
  type ColumnFilterHeaderLabels,
  type ColumnFilterSpec,
} from '@/components/ui/molecules/ColumnFilter';
import { getMatchScoreIntent, getScoreIntent } from '@/utils/tag.utils';
import { formatDate, getDateLabel } from '@/utils/date';
import { CandidateActions } from '../CandidateActions';
import { CandidateIdentityCell } from './CandidateIdentityCell';
import { MatchScoreTag } from './MatchScoreTag';
import {
  formatCvMatchScore,
  formatPercentScore,
  isInterviewExpired,
} from './candidatesTable.utils';

export interface CandidatesTableRow {
  id: string;
  candidate: Candidate;
  talentName: string;
  talentEmail: string;
  source: CandidateSource;
  cvMatch: number;
  fitScore: number | null;
  stageLabel: string;
  stageType: string;
  expiresAt: string | null;
  expirationStatus: 'active' | 'expired' | '';
  canSelect: boolean;
  hasCheatingFlag: boolean;
  jobId: string;
}

const SOURCE_VALUES = ['cv_upload', 'talent_pool', 'talent_cloud'] as const;
type SourceValue = (typeof SOURCE_VALUES)[number];
type SourceLabelKey = `table.source.${SourceValue}`;

function toSourceValue(source: string | undefined): SourceValue {
  return SOURCE_VALUES.includes(source as SourceValue)
    ? (source as SourceValue)
    : 'cv_upload';
}

export function sourceLabelKey(source: string | undefined): SourceLabelKey {
  return `table.source.${toSourceValue(source)}`;
}

export function mapCandidateToRow(args: {
  candidate: Candidate;
  jobId: string;
  fitScore: number | null;
  hasCheatingFlag: boolean;
  canSelect: boolean;
  /** Stage the candidate resolves to in the job's pipeline. */
  stage: PipelineStage | undefined;
  /** Display name of `stage` (see useStageDisplayLabel). */
  stageLabel: string | undefined;
}): CandidatesTableRow {
  const { candidate, jobId, fitScore, hasCheatingFlag, canSelect, stage, stageLabel } = args;
  const expiresAt = candidate.interviewExpiresAt || null;
  const stageType = stage?.stageType ?? candidate.pipelineStageSemantic ?? '';
  const isInterviewing = stageType === 'interviewing';
  return {
    id: candidate.id,
    candidate,
    talentName: candidate.talentName || '',
    talentEmail: candidate.talentEmail || '',
    source: toSourceValue(candidate.source),
    cvMatch: candidate.score ?? 0,
    fitScore,
    stageLabel:
      stageLabel ?? candidate.pipelineStageLabel ?? candidate.pipelineStageSemantic ?? '',
    stageType,
    expiresAt,
    expirationStatus: !isInterviewing
      ? ''
      : isInterviewExpired(expiresAt)
        ? 'expired'
        : 'active',
    canSelect,
    hasCheatingFlag,
    jobId,
  };
}

type JobT = TFunction<['job', 'candidates', 'common']>;

export interface CreateCandidatesTableColumnsOptions {
  t: JobT;
  isSearchMode: boolean;
  /** Semantic of the active stage; undefined when the pipeline is still loading. */
  activeStageType: string | undefined;
  requiresReview: boolean;
  job: JobOffer;
  examConfig: ExamConfig;
  onRefresh?: () => void;
  onExtendInterview?: (
    interviewId: string,
    candidateName: string,
    currentExpiration: string | null
  ) => void;
  onDeleteCandidate?: (candidateId: string, candidateName: string) => void;
}

function sourceFilterSpec(t: JobT): ColumnFilterSpec {
  return {
    type: 'enum',
    label: t('table.col_source'),
    options: SOURCE_VALUES.map((value) => ({
      value,
      label: t(sourceLabelKey(value)),
    })),
  };
}

function expiresFilterSpec(t: JobT): ColumnFilterSpec {
  return {
    type: 'enum',
    label: t('table.col_expires_at'),
    options: [
      { value: 'active', label: t('candidates.filter_active') },
      { value: 'expired', label: t('candidates.filter_expired') },
    ],
  };
}

function sourceFilterLabels(t: JobT): ColumnFilterHeaderLabels {
  return {
    searchPlaceholder: t('candidates.filter_source_all'),
    clear: t('candidates.filter_clear'),
    noOptions: t('table.empty_no_source_match'),
    filterAria: t('candidates.filter_source_aria'),
  };
}

function expiresFilterLabels(t: JobT): ColumnFilterHeaderLabels {
  return {
    searchPlaceholder: t('candidates.filter_all'),
    clear: t('candidates.filter_clear'),
    noOptions: t('table.empty_no_match'),
    filterAria: t('candidates.filter_interviews_aria'),
  };
}

export function createCandidatesTableColumns({
  t,
  isSearchMode,
  activeStageType,
  requiresReview,
  job,
  examConfig,
  onRefresh,
  onExtendInterview,
  onDeleteCandidate,
}: CreateCandidatesTableColumnsOptions): ColumnDef<CandidatesTableRow>[] {
  const sourceSpec = sourceFilterSpec(t);
  const expiresSpec = expiresFilterSpec(t);
  const sourceLabels = sourceFilterLabels(t);
  const expiresLabels = expiresFilterLabels(t);
  // EXPIRES / FIT columns: intentional — shown in cross-status search mode, or when
  // the active browse status needs that field (Interviewing → expires; Interviewed+ → fit).
  // Hidden in default Pending browse. Do not change without product confirmation.
  const showExpires =
    isSearchMode || activeStageType === 'interviewing';
  const showFit =
    isSearchMode ||
    activeStageType === 'interviewed' ||
    activeStageType === 'accepted' ||
    activeStageType === 'rejected' ||
    activeStageType === 'hired';

  const columns: ColumnDef<CandidatesTableRow>[] = [
    {
      accessorKey: 'talentName',
      meta: { label: t('table.col_candidate') },
      header: dataTableSortHeader(t('table.col_candidate')),
      size: 240,
      minSize: 180,
      cell: ({ row }) => (
        <CandidateIdentityCell row={row.original} />
      ),
    },
    {
      accessorKey: 'source',
      meta: { label: t('table.col_source'), filter: sourceSpec },
      filterFn: resolveColumnFilterFn(sourceSpec),
      header: columnFilterHeader(sourceSpec, sourceLabels),
      size: 140,
      minSize: 120,
      enableSorting: false,
      cell: ({ row }) => (
        <Tag intent="secondary" variant="subtle" className="whitespace-nowrap">
          {t(sourceLabelKey(row.original.source))}
        </Tag>
      ),
    },
    {
      accessorKey: 'cvMatch',
      meta: { label: t('table.col_match') },
      header: dataTableSortHeader(t('table.col_match')),
      size: 120,
      minSize: 104,
      cell: ({ row }) => (
        <MatchScoreTag
          scoreLabel={formatCvMatchScore(row.original.candidate.score)}
          intent={getMatchScoreIntent(
            row.original.candidate.score ? row.original.candidate.score / 100 : null
          )}
          status={row.original.candidate.scoringStatus}
          t={t}
        />
      ),
    },
  ];

  if (showExpires) {
    columns.push({
      id: 'expiresAt',
      accessorFn: (row) => row.expirationStatus,
      meta: { label: t('table.col_expires_at'), filter: expiresSpec },
      filterFn: resolveColumnFilterFn(expiresSpec),
      header: columnFilterHeader(expiresSpec, expiresLabels),
      size: 140,
      minSize: 120,
      sortingFn: (rowA, rowB) => {
        const a = rowA.original.expiresAt
          ? new Date(rowA.original.expiresAt).getTime()
          : 0;
        const b = rowB.original.expiresAt
          ? new Date(rowB.original.expiresAt).getTime()
          : 0;
        return a - b;
      },
      cell: ({ row }) => {
        if (row.original.stageType !== 'interviewing') {
          return (
            <Typography variant="body-sm" color="muted">
              -
            </Typography>
          );
        }
        const expired = row.original.expirationStatus === 'expired';
        return (
          <div>
            <Typography
              variant="body-sm"
              weight={expired ? 'semibold' : 'medium'}
              className={expired ? 'text-red-600' : undefined}
            >
              {formatDate(row.original.expiresAt)}
            </Typography>
            {expired && (
              <Typography variant="body-xs" className="text-red-500">
                {getDateLabel('expired')}
              </Typography>
            )}
          </div>
        );
      },
    });
  }

  if (showFit) {
    columns.push({
      accessorKey: 'fitScore',
      meta: { label: t('table.col_fit') },
      header: dataTableSortHeader(t('table.col_fit')),
      size: 96,
      minSize: 80,
      cell: ({ row }) => (
        <Tag intent={getScoreIntent(row.original.fitScore)} variant="subtle">
          {formatPercentScore(row.original.fitScore)}
        </Tag>
      ),
    });
  }

  columns.push(
    {
      accessorKey: 'stageLabel',
      meta: { label: t('table.col_status') },
      header: dataTableSortHeader(t('table.col_status')),
      size: 120,
      minSize: 100,
      enableSorting: false,
      cell: ({ row }) => (
        <StatusPill variant={getCandidateStatusVariant(row.original.stageType)}>
          {row.original.stageLabel}
        </StatusPill>
      ),
    },
    {
      id: 'actions',
      header: dataTableSortHeader(t('table.col_actions')),
      enableSorting: false,
      enableHiding: false,
      size: 200,
      minSize: 160,
      cell: ({ row }) => (
        <div onClick={(event) => event.stopPropagation()}>
          <CandidateActions
            candidate={row.original.candidate}
            job={job}
            examConfig={examConfig}
            onStatusUpdate={onRefresh}
            onExtendInterview={onExtendInterview}
            onDeleteCandidate={onDeleteCandidate}
            requiresReview={requiresReview}
          />
        </div>
      ),
    }
  );

  return columns;
}
